import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FaEye,
  FaSearch,
  FaDownload,
  FaPencilAlt,
  FaTrash,
  FaBan
} from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import './Providers.css';
import Pagination from '../Common/Pagination';
import ConfirmModal from '../Common/ConfirmModal';
import SuspendModal from '../Common/SuspendModal';

const ProvidersList = () => {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState(''); // New state for real-time typing
  const [statusFilter, setStatusFilter] = useState('all');
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    id: null
  });

  const [suspendModal, setSuspendModal] = useState({
    isOpen: false,
    id: null
  });

  const [serviceAreas, setServiceAreas] = useState([]);
  const [selectedAreaId, setSelectedAreaId] = useState('');

  // Debounce logic for search
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(inputValue);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500); // 500ms debounce

    return () => clearTimeout(handler);
  }, [inputValue]);

  const getFullUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const baseUrl = ENDPOINTS.AUTH.LOGIN.replace('/api/auth/login', '');
    return `${baseUrl}/${url.startsWith('/') ? url.slice(1) : url}`;
  };

  const fetchServiceAreas = async () => {
    try {
      const response = await api.get(ENDPOINTS.SERVICE_AREAS.base);
      setServiceAreas(response.data || []);
    } catch (error) {
      console.error('Failed to fetch service areas:', error);
    }
  };

  const fetchProviders = useCallback(async () => {
    if (isFirstLoad) setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        status: statusFilter || undefined,
        search: searchTerm || undefined,
        areaIds: selectedAreaId ? [parseInt(selectedAreaId)] : undefined
      };
      const response = await api.get(ENDPOINTS.PROVIDERS.base, { params });
      const providersData = response.data || [];
      setProviders(providersData.data || providersData);
      setPagination(prev => ({ ...prev, total: providersData.meta?.total || response.total || 0 }));
    } catch (error) {
      console.error('Failed to fetch providers:', error);
    } finally {
      setLoading(false);
      setIsFirstLoad(false);
    }
  }, [pagination.page, pagination.limit, statusFilter, searchTerm, selectedAreaId, isFirstLoad]);

  useEffect(() => {
    fetchServiceAreas();
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const handleDelete = (id) => {
    setDeleteModal({ isOpen: true, id });
  };

  const confirmDelete = async () => {
    const { id } = deleteModal;
    if (!id) return;
    
    try {
      await api.delete(`${ENDPOINTS.PROVIDERS.base}/${id}`);
      toast.success('Provider deleted successfully');
      setDeleteModal({ isOpen: false, id: null });
      fetchProviders();
    } catch (error) {
      console.error('Failed to delete provider:', error);
      const errorMsg = error.response?.data?.message || 'Failed to delete provider. There might be active bookings or other records linked to this provider.';
      toast.error(errorMsg);
    }
  };

  const handleSuspendSubmit = async (reason) => {
    const { id } = suspendModal;
    try {
      await api.post(`${ENDPOINTS.PROVIDERS.base}/${id}/suspend`, { reason });
      toast.success('Provider suspended successfully');
      setSuspendModal({ isOpen: false, id: null });
      fetchProviders();
    } catch (error) {
      console.error('Failed to suspend provider:', error);
      toast.error(error.response?.data?.message || 'Failed to suspend provider');
    }
  };

  const getStatusClass = (status) => {
    const s = status ? status.toLowerCase() : '';
    return s;
  };

  if (loading && isFirstLoad) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Fetching providers...</p>
      </div>
    );
  }

  return (
    <div className="providers-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1>Providers</h1>
          <p className="page-subtitle">Review and manage provider onboarding requests</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => navigate('/providers/create')}>
            Add New Provider
          </button>
          {/* <button className="btn btn-primary" onClick={() => navigate('/providers/approval')}>
            Provider Management
          </button> */}
          {/* <button className="btn btn-secondary" onClick={() => toast.info('Exporting...')}>
            <FaDownload /> Export List
          </button> */}
          {/* <button className="btn btn-secondary">
            Filter Providers
          </button> */}
        </div>
      </div>

      <div className="content-card filters-container-refined">
        <div className="status-tabs-premium">
          <button
            className={`status-tab ${statusFilter === 'all' || !statusFilter ? 'active' : ''}`}
            onClick={() => {
              setStatusFilter('all');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            All Providers
          </button>
          <button
            className={`status-tab ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => {
              setStatusFilter('pending');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            Pending
          </button>
          <button
            className={`status-tab ${statusFilter === 'approved' ? 'active' : ''}`}
            onClick={() => {
              setStatusFilter('approved');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            Approved
          </button>
          <button
            className={`status-tab ${statusFilter === 'rejected' ? 'active' : ''}`}
            onClick={() => {
              setStatusFilter('rejected');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            Rejected
          </button>
          <button
            className={`status-tab ${statusFilter === 'suspended' ? 'active' : ''}`}
            onClick={() => {
              setStatusFilter('suspended');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            Suspended
          </button>
        </div>

        <div className="filters-right-actions">
          <div className="search-bar-refined">
            <FaSearch className="search-icon" />
            <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                style={{ width: '100%', paddingRight: '30px' }}
              />
              {inputValue && (
                <button 
                  onClick={() => setInputValue('')}
                  style={{ position: 'absolute', right: '8px', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0', fontSize: '16px', display: 'flex', alignItems: 'center' }}
                  title="Clear search"
                >
                  &#x2715;
                </button>
              )}
            </div>
          </div>

          <select
            className="filter-select-premium"
            value={selectedAreaId}
            onChange={(e) => {
              setSelectedAreaId(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            <option value="">All Service Areas</option>
            {serviceAreas.map(area => (
              <option key={area.id} value={area.id}>{area.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Providers Table */}
      <div className="content-card" style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        <div className="table-responsive">
          <table className="table table-hover align-middle custom-table">
            <thead>
              <tr>
                <th>PROVIDER</th>
                <th>CUSTOMER</th>
                {/* <th>SERVICE CATEGORY</th> */}
                {/* <th>DOCUMENTS STATUS</th> */}
                <th className="text-center">BACKGROUND CHECK STATUS</th>
                <th className="text-center">SUBMITTED DATE</th>
                <th className="text-center">STATUS</th>
                <th className="text-center">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {providers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty-state">
                    <div className="empty-state-content">
                      <div className="empty-state-icon">👥</div>
                      <h3>No providers found</h3>
                      <p>Try adjusting your search or filter criteria</p>
                    </div>
                  </td>
                </tr>
              ) : (
                providers.map((provider) => (
                  <tr
                    key={provider.id}
                    onClick={() => navigate(`/providers/${provider.id}`)}
                    style={{ cursor: 'pointer' }}
                    className="booking-row-clickable"
                  >
                    <td>
                      <div className="user-cell">
                        <img
                          src={getFullUrl(provider.avatar) || `https://ui-avatars.com/api/?name=${provider.firstName}+${provider.lastName}`}
                          alt={`${provider.firstName} ${provider.lastName}`}
                          className="user-avatar"
                        />
                        <span className="provider-name">{provider.firstName} {provider.lastName}</span>
                      </div>
                    </td>
                    <td>
                      <span className="customer-email">{provider.email}</span>
                    </td>
                    {/* <td>
                      <div className="services-tags">
                        <span className="service-tag">
                          {provider.specialization || 'General'}
                        </span>
                      </div>
                    </td> */}
                    {/* <td>
                      <span className={`doc-status ${provider.isProfileComplete ? 'complete' : 'pending'}`}>
                        {provider.isProfileComplete ? 'Complete' : 'Pending'}
                      </span>
                    </td> */}
                    <td className="text-center">
                      <span className={`bg-status ${provider.status === 'approved' ? 'verified' : provider.status === 'rejected' ? 'rejected' : 'pending'}`}>
                        {provider.status === 'approved' ? 'Verified' : provider.status === 'rejected' ? 'Rejected' : 'Pending'}
                      </span>
                    </td>
                    <td className="text-center">{new Date(provider.createdAt).toLocaleDateString()}</td>
                    <td className="text-center">
                      <span className={`status-badge ${getStatusClass(provider.status)}`}>
                        {provider.status}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="action-buttons-manage" style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                        <Link
                          to={`/providers/${provider.id}`}
                          className="action-btn view"
                          title="View Details"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FaEye />
                        </Link>
                        <Link
                          to={`/providers/${provider.id}/edit`}
                          className="action-btn edit"
                          title="Edit Provider"
                          style={{ color: '#fed330', border: 'none', background: 'none', cursor: 'pointer', padding: '5px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FaPencilAlt />
                        </Link>
                        {provider.status !== 'rejected' && (
                          <button
                            className="action-btn suspend"
                            onClick={(e) => { e.stopPropagation(); setSuspendModal({ isOpen: true, id: provider.id }); }}
                            title="Suspend Provider"
                            style={{ color: '#f97316', border: 'none', background: 'none', cursor: 'pointer', padding: '5px' }}
                          >
                            <FaBan />
                          </button>
                        )}
                        {/* <button
                          className="action-btn delete"
                          onClick={(e) => { e.stopPropagation(); handleDelete(provider.id); }}
                          title="Delete Provider"
                          style={{ color: '#ff4d4f', border: 'none', background: 'none', cursor: 'pointer', padding: '5px' }}
                        >
                          <FaTrash />
                        </button> */}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        />

        {providers.length > 0 && (
          <div className="table-footer">
            <p>Showing {providers.length} of {pagination.total} providers</p>
          </div>
        )}
      </div>

      <ConfirmModal 
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, id: null })}
        onConfirm={confirmDelete}
        title="Delete Provider Profile?"
        message="Warning: This will permanently delete the provider and all their associated bookings, payouts, and reviews. This action cannot be undo."
        confirmText="Permanently Delete"
      />

      <SuspendModal
        isOpen={suspendModal.isOpen}
        onClose={() => setSuspendModal({ isOpen: false, id: null })}
        onConfirm={handleSuspendSubmit}
        title="Suspend Provider Account"
        message="Please provide a reason for suspending this provider. They will not be able to log in or accept new bookings while suspended."
        confirmText="Suspend Provider"
      />
    </div>
  );
};

export default ProvidersList;